import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { ArrowRight, Building2 } from 'lucide-react';
import { api, ApiError, type PublicInvoiceDTO } from '../lib/api.ts';
import { clearBuyerSession, readBuyerSession } from '../lib/buyer-session.ts';
import { isPaymentUnderway, nextStopForBuyer } from '../lib/buyer-flow.ts';

// Buyer's first landing screen. Shows seller branding + amount + line items
// without authentication; the CTA gates the rest of the flow. If the buyer
// already holds an active session for this invoice (the magic-link cookie
// is still alive), the CTA short-circuits the magic-link round-trip and
// jumps straight to the next stop in the buyer flow.
//
// Two flavors share this page:
//   - mode='invoice' (default): the dashboard-created flow. Seller's NCM /
//     line items render from `data.lineItems`; buyer signs in to a magic
//     link previously emailed by the seller.
//   - mode='intent': payment intent created by an integrator via
//     /v1/payments. Renders the integrator-supplied `display` block
//     instead of NCM line items, and post-payment redirect honors
//     `data.returnUrl`. Buyer KYC is still required — same journey from
//     /r/$publicId/signin onwards.
function InvoiceSummaryPage() {
  const { publicId } = Route.useParams();
  const intl = useIntl();
  const navigate = useNavigate();
  const { data, error, isLoading, refetch } = useQuery<PublicInvoiceDTO, ApiError>({
    queryKey: ['public-invoice', publicId],
    queryFn: () => api.getPublicInvoice(publicId),
  });

  // If the buyer already has a valid session for this invoice, skip the
  // magic-link round-trip. We probe the buyer-KYC endpoint (cookie-gated)
  // to confirm the cookie is still alive — a 401/403 means it expired and
  // we fall back to the sign-in CTA.
  const buyerId = useMemo(() => {
    const cached = readBuyerSession();
    return cached && cached.publicId === publicId ? cached.buyerId : null;
  }, [publicId]);
  const kycProbe = useQuery({
    queryKey: ['buyer-kyc', buyerId],
    enabled: buyerId !== null,
    retry: false,
    queryFn: async () => {
      try {
        // Opt out of the global 401 kick-out: this is a public landing page,
        // so an expired session should fall back to the sign-in CTA below
        // (via the clearBuyerSession effect) rather than hard-redirect.
        return await api.getBuyerKyc(buyerId!, { skipAuthRedirect: true });
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
  });
  useEffect(() => {
    const e = kycProbe.error;
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      clearBuyerSession();
    }
  }, [kycProbe.error]);
  const sessionAuthenticated = buyerId !== null && kycProbe.isSuccess;
  const [continuing, setContinuing] = useState(false);

  async function continueToPay(): Promise<void> {
    if (!data || !sessionAuthenticated) return;
    setContinuing(true);
    try {
      const next = nextStopForBuyer({
        invoiceStatus: data.status,
        kycStatus: kycProbe.data ? kycProbe.data.status : null,
      });
      const target =
        next === 'kyc'
          ? '/r/$publicId/kyc'
          : next === 'on-ramp'
            ? '/r/$publicId/on-ramp'
            : '/r/$publicId/tracking';
      await navigate({ to: target, params: { publicId } });
    } catch {
      setContinuing(false);
    }
  }

  // If the payment is already in flight (funds sent / off-ramp begun) or the
  // invoice has reached a terminal state, there is nothing for the buyer to
  // (re-)initiate here. Send them straight to the read-only tracking timeline
  // instead of re-offering the pay CTA — the tracking page is public, so this
  // works whether or not the buyer still holds a session. `replace` keeps the
  // invoice page out of history so Back doesn't bounce them into this redirect.
  const redirectToTracking = data ? isPaymentUnderway(data.status) : false;
  useEffect(() => {
    if (redirectToTracking) {
      void navigate({ to: '/r/$publicId/tracking', params: { publicId }, replace: true });
    }
  }, [redirectToTracking, navigate, publicId]);

  if (isLoading || redirectToTracking) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 text-[var(--color-navy-600)]">
        <FormattedMessage id="common.loading" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <p className="text-[var(--color-danger)]">
          {error.status === 404 ? <FormattedMessage id="invoice.error.notFound" /> : error.message}
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-4 inline-flex items-center gap-2 rounded-md border border-[var(--color-navy-200)] px-3 py-1.5 text-sm text-[var(--color-navy-800)] hover:bg-[var(--color-navy-50)]"
        >
          <FormattedMessage id="common.retry" />
        </button>
      </div>
    );
  }

  if (!data) return null;

  const amount = Number(data.amount);
  const isIntent = data.mode === 'intent';
  const intentDisplay = isIntent ? (data.display ?? null) : null;
  // Intent display.lineItems is integrator-supplied free-form; the relational
  // lineItems list (NCM/origin) is the dashboard-CMS shape. Render whichever
  // is populated, never both.
  const intentLineItems = intentDisplay?.lineItems ?? [];
  const showIntentLineItems = isIntent && intentLineItems.length > 0;
  const showInvoiceLineItems = !isIntent && data.lineItems && data.lineItems.length > 0;

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <div className="rounded-xl border border-[var(--color-navy-100)] bg-white p-6 shadow-sm">
        <div className="flex items-center gap-3">
          {data.seller.logoUrl ? (
            <img src={data.seller.logoUrl} alt="" className="h-10 w-10 rounded-md object-cover" />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[var(--color-navy-100)] text-[var(--color-navy-700)]">
              <Building2 size={20} strokeWidth={1.5} aria-hidden="true" />
            </div>
          )}
          <h1 className="text-xl font-semibold text-[var(--color-navy-900)]">
            {intentDisplay?.title ??
              intl.formatMessage({ id: 'invoice.title' }, { seller: data.seller.name })}
          </h1>
        </div>

        <div className="mt-6 flex items-baseline justify-between border-y border-[var(--color-navy-100)] py-4">
          <span className="text-sm text-[var(--color-navy-600)]">
            <FormattedMessage id="invoice.amount.label" />
          </span>
          <span className="font-mono text-2xl font-semibold text-[var(--color-navy-900)]">
            <FormattedNumber
              value={amount}
              style="currency"
              currency={data.currency}
              minimumFractionDigits={2}
              maximumFractionDigits={2}
            />
          </span>
        </div>

        {showInvoiceLineItems && (
          <section className="mt-6">
            <h2 className="text-sm font-medium uppercase tracking-wide text-[var(--color-navy-500)]">
              <FormattedMessage id="invoice.lineItems.heading" />
            </h2>
            <ul className="mt-2 divide-y divide-[var(--color-navy-100)]">
              {data.lineItems!.map((li) => (
                <li key={li.id} className="flex items-start justify-between gap-4 py-3 text-sm">
                  <div>
                    <p className="font-medium text-[var(--color-navy-900)]">{li.description}</p>
                    <p className="text-xs text-[var(--color-navy-500)]">
                      {intl.formatMessage(
                        { id: 'invoice.lineItem.qty' },
                        {
                          qty: li.quantity,
                          unitPrice: intl.formatNumber(Number(li.unitPrice), {
                            style: 'currency',
                            currency: data.currency,
                          }),
                        },
                      )}
                    </p>
                  </div>
                  <span className="font-mono text-[var(--color-navy-800)]">
                    <FormattedNumber
                      value={Number(li.subtotal)}
                      style="currency"
                      currency={data.currency}
                    />
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {showIntentLineItems && (
          <section className="mt-6">
            <h2 className="text-sm font-medium uppercase tracking-wide text-[var(--color-navy-500)]">
              <FormattedMessage id="invoice.lineItems.heading" />
            </h2>
            <ul className="mt-2 divide-y divide-[var(--color-navy-100)]">
              {intentLineItems.map((li, idx) => (
                <li
                  key={`${idx}-${li.description}`}
                  className="flex items-start justify-between gap-4 py-3 text-sm"
                >
                  <div>
                    <p className="font-medium text-[var(--color-navy-900)]">{li.description}</p>
                    {li.quantity && li.unitPrice ? (
                      <p className="text-xs text-[var(--color-navy-500)]">
                        {intl.formatMessage(
                          { id: 'invoice.lineItem.qty' },
                          {
                            qty: li.quantity,
                            unitPrice: intl.formatNumber(Number(li.unitPrice), {
                              style: 'currency',
                              currency: data.currency,
                            }),
                          },
                        )}
                      </p>
                    ) : null}
                  </div>
                  {li.subtotal ? (
                    <span className="font-mono text-[var(--color-navy-800)]">
                      <FormattedNumber
                        value={Number(li.subtotal)}
                        style="currency"
                        currency={data.currency}
                      />
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        )}

        {intentDisplay?.notes ? (
          <p className="mt-6 whitespace-pre-line text-sm text-[var(--color-navy-700)]">
            {intentDisplay.notes}
          </p>
        ) : null}

        <div className="mt-8 flex justify-end">
          {sessionAuthenticated ? (
            <button
              type="button"
              onClick={() => void continueToPay()}
              disabled={continuing}
              className="inline-flex items-center gap-2 rounded-md bg-[var(--color-navy-800)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-navy-700)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FormattedMessage id="invoice.cta.continue" />
              <ArrowRight size={16} strokeWidth={1.5} aria-hidden="true" />
            </button>
          ) : (
            <Link
              to="/r/$publicId/signin"
              params={{ publicId }}
              // Cached session probe still in flight: we let the link render
              // anyway; the worst case is a click during the probe window
              // sends the buyer to /signin, which is still correct.
              className="inline-flex items-center gap-2 rounded-md bg-[var(--color-navy-800)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-navy-700)]"
            >
              <FormattedMessage id="invoice.cta.signin" />
              <ArrowRight size={16} strokeWidth={1.5} aria-hidden="true" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/r/$publicId/')({
  component: InvoiceSummaryPage,
});
